import type { FC, ReactNode } from 'react';

import { Box } from '@mui/material';

import Logo from 'src/components/Logo';
import { TV_COLORS } from 'src/tv/tvTheme';

interface TvFrameProps {
  /** Right-hand side of the top bar. */
  aside?: ReactNode;
  children: ReactNode;
  /** Tighter margins for the dashboards, which need the whole screen for their tables. */
  isDense?: boolean;
}

/**
 * Full-viewport shell for a wall screen. Sizes are viewport-relative (vw/vh) rather than rem on
 * purpose: the same page must read from across a room on a 1080p TV, a 4K TV and a laptop.
 */
const TvFrame: FC<TvFrameProps> = (props) => {
  const { aside, children, isDense } = props;

  return (
    <Box
      component="main"
      sx={{
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: TV_COLORS.background,
        color: TV_COLORS.text,
        px: isDense ? '2.6vw' : '4.5vw',
        py: isDense ? '2.2vh' : '4vh',
        cursor: 'none',
      }}
    >
      <Box
        component="header"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2vw' }}
      >
        {/* The logo's viewBox is 156 × 52 with the mark in the top 32 units; the negative margin
            trims the empty bottom so the mark lines up with the right-hand side. */}
        <Logo
          alwaysWhite
          sx={isDense
            ? { height: '4.5vh', width: '13.5vh', mb: '-1.7vh', display: 'block' }
            : { height: '6vh', width: '18vh', mb: '-2.3vh', display: 'block' }}
        />
        {aside}
      </Box>
      {children}
    </Box>
  );
};

export default TvFrame;
